import { RolePermission } from 'src/app/models/rolePermission.model';
import { PERMISSION_COLUMNS, hasColumn } from './role-permission-verb.util';

/**
 * The change between a role's last-saved (baseline) mask and its current in-memory (working) mask,
 * summarised for the save bar (DASH-3179, part 6/9; design-spec §5.6, §8).
 *
 * - `added` / `removed` count **column grants**, not objects — the `+N` / `−N` in the save bar. A
 *   column (Read · Create/Update · Delete · Admin) is "granted" when {@link hasColumn} is true, so a
 *   sub-column bit change that does not flip a whole column is a dirty object but not an add/remove.
 * - `objectCount` is the number of distinct objects whose mask changed at all — the "across N objects"
 *   figure and the source of the dirty-object set.
 * - `dirtyObjectIds` backs the per-row amber edge and the `Changed` filter (part 7).
 */
export interface RolePermissionDiff {
  added: number;
  removed: number;
  objectCount: number;
  dirtyObjectIds: ReadonlySet<string>;
}

/** The empty diff — a role with no unsaved changes. */
export const EMPTY_ROLE_PERMISSION_DIFF: RolePermissionDiff = {
  added: 0,
  removed: 0,
  objectCount: 0,
  dirtyObjectIds: new Set<string>(),
};

/**
 * Diffs a working mask set against its baseline. Both are keyed by objectId and clamped to the valid
 * 8-bit range, so an object present in only one side is compared against a zero mask (a granted row the
 * server never stored, or a row cleared to nothing, both register). The comparison is per column
 * through {@link hasColumn}, so it never depends on the raw bit layout.
 */
export function diffRolePermissions(
  baseline: readonly RolePermission[] | null | undefined,
  working: readonly RolePermission[] | null | undefined,
): RolePermissionDiff {
  const baseById = maskById(baseline);
  const workById = maskById(working);

  let added = 0;
  let removed = 0;
  const dirtyObjectIds = new Set<string>();

  for (const objectId of new Set([...baseById.keys(), ...workById.keys()])) {
    const baseMask = baseById.get(objectId) ?? 0;
    const workMask = workById.get(objectId) ?? 0;
    if (baseMask === workMask) {
      continue;
    }
    dirtyObjectIds.add(objectId);
    for (const column of PERMISSION_COLUMNS) {
      const wasGranted = hasColumn(baseMask, column.mask);
      const nowGranted = hasColumn(workMask, column.mask);
      if (nowGranted && !wasGranted) {
        added++;
      } else if (!nowGranted && wasGranted) {
        removed++;
      }
    }
  }

  return { added, removed, objectCount: dirtyObjectIds.size, dirtyObjectIds };
}

function maskById(permissions: readonly RolePermission[] | null | undefined): Map<string, number> {
  return new Map((permissions ?? []).map(permission => [permission.objectId, (permission.permissions ?? 0) & 0xff]));
}
